import { c as _c } from "react/compiler-runtime";
import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { AnalyticsProvideFeedbackButton } from "@components/analytics/in-house/feedback/analytics-provide-feedback-button";
import { useAnalytics } from "@hooks/analytics/use-analytics";
import { GroupsAnalytics } from "@pages/analytics/groups-analytics";
import { InHouseAnalyticsPage } from "@pages/analytics/in-house-analytics-page";
import { MembersAnalytics } from "@pages/analytics/members-analytics";
import { ProjectsAnalytics } from "@pages/analytics/projects-analytics";
import { WorkspaceAnalytics } from "@pages/analytics/workspace-analytics";
import { NotFoundPage } from "@pages/not-found-page";
import { AnalyticsTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { appStateSelector } from "@store/app/app-selector";
import { useAppSelector } from "@store/store-helper";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";

/** Whether premium analytics are not available for the current environment */
const isPremiumAnalyticsNotAvailable = runtimeConfig.appEnv === "eu-dev" || runtimeConfig.appEnv === "com-staging" || runtimeConfig.appEnv === "eu-staging";

/** Renders the contents in analytics page */
export function AnalyticsPage() {
  const $ = _c(25);
  const {
    analyticsTabs
  } = useAppParams();
  useAnalytics();
  const {
    isAlphaTestingEnabled
  } = useAppSelector(appStateSelector);
  const {
    canViewPremiumAnalytics
  } = useHasUserValidFeatureCompanyLevel();
  const isPremiumAnalyticsHidden = isPremiumAnalyticsNotAvailable || !canViewPremiumAnalytics || isAlphaTestingEnabled;
  if (analyticsTabs === undefined || !Object.values(AnalyticsTabs).includes(analyticsTabs)) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <NotFoundPage />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      label: "Workspace",
      route: AnalyticsTabs.workspace,
      content: _temp,
      loadingContent: <WorkspaceAnalytics />,
      requiredFeatureCompanyLevel: RequiredFeatureCompanyLevelName.canViewAnalytics
    };
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ProjectsAnalytics />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AnalyticsProvideFeedbackButton />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isPremiumAnalyticsHidden) {
    t3 = {
      label: "Projects",
      route: AnalyticsTabs.projects,
      content: _temp2,
      loadingContent: t1,
      requiredFeatureCompanyLevel: RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
      isHidden: isPremiumAnalyticsHidden,
      actionButtons: t2
    };
    $[4] = isPremiumAnalyticsHidden;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MembersAnalytics />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <AnalyticsProvideFeedbackButton />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isPremiumAnalyticsHidden) {
    t6 = {
      label: "Members",
      route: AnalyticsTabs.members,
      content: _temp3,
      loadingContent: t4,
      requiredFeatureCompanyLevel: RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
      isHidden: isPremiumAnalyticsHidden,
      actionButtons: t5
    };
    $[8] = isPremiumAnalyticsHidden;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <GroupsAnalytics />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <AnalyticsProvideFeedbackButton />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== isPremiumAnalyticsHidden) {
    t9 = {
      label: "Groups",
      route: AnalyticsTabs.groups,
      content: _temp4,
      loadingContent: t7,
      requiredFeatureCompanyLevel: RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
      isHidden: isPremiumAnalyticsHidden,
      actionButtons: t8
    };
    $[12] = isPremiumAnalyticsHidden;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <InHouseAnalyticsPage />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = !isAlphaTestingEnabled;
  let t12;
  if ($[15] !== t11) {
    t12 = {
      label: "In-House Analytics",
      route: AnalyticsTabs.inHouseAnalytics,
      content: _temp5,
      loadingContent: t10,
      requiredFeatureCompanyLevel: RequiredFeatureCompanyLevelName.canViewPremiumAnalytics,
      isHidden: t11
    };
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== t3 || $[19] !== t6 || $[20] !== t9) {
    t13 = [t0, t3, t6, t9, t12];
    $[17] = t12;
    $[18] = t3;
    $[19] = t6;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== analyticsTabs || $[23] !== t13) {
    t14 = <TabsWithRoutes selectedTab={analyticsTabs} dataTestId="ANALYTICS_CONTAINER" requiredAttribute={false} trackingEventPageName={OpenTabEvents.openAnalyticsTab} tabs={t13} />;
    $[22] = analyticsTabs;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
}
function _temp5() {
  return <InHouseAnalyticsPage />;
}
function _temp4() {
  return <GroupsAnalytics />;
}
function _temp3() {
  return <MembersAnalytics />;
}
function _temp2() {
  return <ProjectsAnalytics />;
}
function _temp() {
  return <WorkspaceAnalytics />;
}